import { processListingParams } from '@/api/helpers';
import { ListingRequest, ListingResponse } from '@/api/types';
import {
  AdminTask,
  AdminTaskPayload,
  AdminTasksResultWithCount,
} from '@/client/admin-tasks/types';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { MessageResponseModel } from '@/client/models';
import { Tag } from '@/client/tags';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

export const adminTasksService = (client: AxiosInstance) => {
  const ADMIN_TASKS_BASE_URL = '/admin-tasks';

  const getAdminTaskTags = async (): Promise<Tag[]> => {
    try {
      const result = await client.get<
        ListingResponse<Tag>,
        AxiosResponse<ListingResponse<Tag>>
      >('/admin-task-tags', {
        withCredentials: true,
      });

      return result.data.result;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getAdminTasks = async (
    params?: ListingRequest,
  ): Promise<AdminTasksResultWithCount> => {
    try {
      const result = await client.get<
        AdminTasksResultWithCount,
        AxiosResponse<AdminTasksResultWithCount>
      >(ADMIN_TASKS_BASE_URL, {
        withCredentials: true,
        ...(params ? { params: processListingParams(params) } : {}),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getAdminTask = async (taskId: string): Promise<AdminTask> => {
    try {
      const result = await client.get<AdminTask, AxiosResponse<AdminTask>>(
        `${ADMIN_TASKS_BASE_URL}/${taskId}`,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const createAdminTask = async (
    request: AdminTaskPayload,
  ): Promise<AdminTask> => {
    try {
      const result = await client.post<AdminTask, AxiosResponse<AdminTask>>(
        ADMIN_TASKS_BASE_URL,
        request,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateAdminTask = async (
    taskId: string,
    request: AdminTaskPayload,
  ): Promise<AdminTask> => {
    try {
      const result = await client.patch<AdminTask, AxiosResponse<AdminTask>>(
        `${ADMIN_TASKS_BASE_URL}/${taskId}`,
        request,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteAdminTask = async (
    taskId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ADMIN_TASKS_BASE_URL}/${taskId}`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  return {
    getAdminTaskTags,
    getAdminTasks,
    getAdminTask,
    createAdminTask,
    updateAdminTask,
    deleteAdminTask,
  };
};
